// eslint-disable-next-line
import { Box } from '@mui/material';
import clsx from 'clsx';
import { useDispatch, useSelector } from 'react-redux';

import { getActiveAssetActiveZone } from 'GeminiViewerComponent/features/asset/assetSlice';
import { getConfig } from 'GeminiViewerComponent/features/config/configSlice';
import { selectActiveTheme } from 'GeminiViewerComponent/features/globals/themeSlice';
import { useSetSplitModePosition } from 'GeminiViewerComponent/hooks/useSetSplitModePosition';
import { loadZone, selectZoneLoadingStatus } from 'shared/loadZoneSlice';
import { scenePreviewsStyles } from './styles';

const ScenePreviews = ({
    open,
    onAssetClick,
    onOtherAssetCategoryClick,
    onCategoryClick,
    zonesByCategory,
    otherAssetsByCategory,
    searchMenu,
    controlMenu,
}) => {
    const dispatch = useDispatch();

    const config = useSelector(getConfig);
    const activeZone = useSelector(getActiveAssetActiveZone);
    const zoneLoadingStatus = useSelector(selectZoneLoadingStatus);
    const theme = useSelector(selectActiveTheme);
    const classes = scenePreviewsStyles(theme);
    const activeZoneId = activeZone == null ? '' : activeZone.zone_id;
    const menu = useSetSplitModePosition({ searchMenu, controlMenu });
    const categoryNames = zonesByCategory
        ? Object.keys(zonesByCategory).sort((a, b) =>
              a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' })
          )
        : [];
    const otherAssetNames = otherAssetsByCategory ? Object.keys(otherAssetsByCategory) : [];

    async function handleClick(zone) {
        if (zoneLoadingStatus === 'Loaded') {
            dispatch(loadZone({ zoneId: zone.zone_id }));
        }
    }

    const blocks = [];

    // Current asset categories first
    for (const categoryIdx in categoryNames) {
        const categoryName = categoryNames[categoryIdx];
        const category = zonesByCategory[categoryName];

        const activeAssetZoneComponents = [];
        for (const idx in (category.zones ?? []).sort((a, b) => {
            if (!a.display_order && b.display_order) return -1;
            if (a.display_order && !b.display_order) return 1;
            if (a.display_order && b.display_order)
                return a.display_order
                    ?.toString()
                    ?.localeCompare(b.display_order?.toString(), undefined, { numeric: true });

            return a.display_name.localeCompare(b.display_name);
        })) {
            const zone = category.zones[idx];
            const previewUrl = zone.equirect_image_preview_url
                ? zone.equirect_image_preview_url
                : zone.flat_image_preview_url
                ? zone.flat_image_preview_url
                : zone.pano_id && config.paths.BASE_STORE_URL
                ? config.paths.BASE_STORE_URL + `tiles/${zone.pano_id}/1/f/0/0.jpg`
                : '';

            activeAssetZoneComponents.push(
                <div
                    id={`viewer_title_zone-nav-${categoryIdx}_item-${idx}`}
                    key={`cat_${categoryIdx}_zone_${idx}`}
                    data-zone-id={zone.zone_id}
                    className={clsx(
                        classes.scenePreviewContainer,
                        {
                            [classes.sceneActive]: zone.zone_id === activeZoneId,
                        },
                        `viewer_title_zone-nav-${categoryIdx}_item`,
                        'viewer_title_zone-nav_item'
                    )}
                    style={{
                        backgroundColor: zone?.asset_name && '#abcad0',
                        color: zone?.asset_name && '#000000',
                    }}
                    onClick={() => handleClick(zone)}
                >
                    <Box style={{ width: '100%' }}>
                        {previewUrl ? (
                            <img
                                className={clsx(classes.sceneImage, {
                                    [classes.sceneActive]: zone.zone_id === activeZoneId,
                                })}
                                src={previewUrl}
                                alt={`${previewUrl}`.toLowerCase()}
                            />
                        ) : (
                            <div
                                className={clsx(classes.sceneNoImage, {
                                    [classes.sceneActive]: zone.zone_id === activeZoneId,
                                })}
                            >
                                No Image
                            </div>
                        )}
                        <div
                            className={clsx(classes.scenePreviewTitle, {
                                [classes.scenePreviewTitleActive]: zone.zone_id === activeZoneId,
                            })}
                            style={{
                                backgroundColor: zone?.asset_name && '#abcad0',
                                color: zone?.asset_name && '#000000',
                            }}
                        >
                            {zone.display_name}
                        </div>
                    </Box>
                </div>
            );
        }

        blocks.push(
            <div className={classes.categoryContainer} key={`cat_${categoryIdx}`}>
                <div
                    id={`viewer_title_zone-nav-category-${categoryIdx}`}
                    className={clsx(classes.categoryTitle, 'viewer_title_zone-nav-category')}
                    style={{ ...category?.category_color }}
                    onClick={() => onCategoryClick(categoryName)}
                >
                    {categoryName}
                </div>
                <div
                    className={clsx(classes.categoryZones, {
                        categoryOpen: category.open,
                    })}
                >
                    {activeAssetZoneComponents}
                </div>
            </div>
        );
    }

    // Other assets next as asset accordions -> category accordions -> zones
    for (const assetIdx in otherAssetNames) {
        const assetName = otherAssetNames[assetIdx];
        const asset = otherAssetsByCategory[assetName];
        const assetCategoryNames = Object.keys(asset?.categories ?? {}).sort((a, b) =>
            a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' })
        );
        const assetCategoryBlocks = [];
        for (const zcIdx in assetCategoryNames) {
            const cName = assetCategoryNames[zcIdx];
            const cObj = asset.categories[cName];

            const zoneComponents = [];
            for (const idx in cObj.zones) {
                const zone = cObj.zones[idx];
                const previewUrl = zone.equirect_image_preview_url
                    ? zone.equirect_image_preview_url
                    : zone.flat_image_preview_url
                    ? zone.flat_image_preview_url
                    : zone.pano_id && config.paths.BASE_STORE_URL
                    ? config.paths.BASE_STORE_URL + `tiles/${zone.pano_id}/1/f/0/0.jpg`
                    : '';

                zoneComponents.push(
                    <div
                        id={`viewer_title_zone-nav-${assetIdx}_${zcIdx}_item-${idx}`}
                        key={`asset_${assetIdx}_cat_${zcIdx}_zone_${idx}`}
                        data-zone-id={zone.zone_id}
                        className={clsx(
                            classes.scenePreviewContainer,
                            {
                                [classes.sceneActive]: zone.zone_id === activeZoneId,
                            },
                            `viewer_title_zone-nav-${assetIdx}_${zcIdx}_item`,
                            'viewer_title_zone-nav_item'
                        )}
                        style={{
                            backgroundColor: zone?.asset_name && '#abcad0',
                            color: zone?.asset_name && '#000000',
                        }}
                        onClick={() => handleClick(zone)}
                    >
                        <Box style={{ width: '100%' }}>
                            {previewUrl ? (
                                <img
                                    className={clsx(classes.sceneImage, {
                                        [classes.sceneActive]: zone.zone_id === activeZoneId,
                                    })}
                                    src={previewUrl}
                                    alt={`${previewUrl}`.toLowerCase()}
                                />
                            ) : (
                                <div
                                    className={clsx(classes.sceneNoImage, {
                                        [classes.sceneActive]: zone.zone_id === activeZoneId,
                                    })}
                                >
                                    No Image
                                </div>
                            )}
                            <div
                                className={clsx(classes.scenePreviewTitle, {
                                    [classes.scenePreviewTitleActive]: zone.zone_id === activeZoneId,
                                })}
                                style={{
                                    backgroundColor: zone?.asset_name && '#abcad0',
                                    color: zone?.asset_name && '#000000',
                                }}
                            >
                                {zone.display_name}
                            </div>
                        </Box>
                    </div>
                );
            }

            assetCategoryBlocks.push(
                <div className={classes.categoryContainer} key={`asset_${assetIdx}_cat_${zcIdx}`}>
                    <div
                        id={`viewer_title_zone-nav-other-asset-category-${assetIdx}_${zcIdx}`}
                        className={clsx(classes.categoryTitle, 'viewer_title_zone-nav-category')}
                        style={{ ...cObj?.category_color }}
                        onClick={() => onOtherAssetCategoryClick(assetName, cName)}
                    >
                        {cName}
                    </div>
                    <div
                        className={clsx(classes.categoryZones, {
                            categoryOpen: cObj.open,
                        })}
                    >
                        {zoneComponents}
                    </div>
                </div>
            );
        }
        blocks.push(
            <div className={classes.categoryContainer} key={`other_asset_${assetIdx}`}>
                <div
                    id={`viewer_title_zone-nav-other-asset-${assetIdx}`}
                    className={clsx(classes.categoryTitle, 'viewer_title_zone-nav-asset')}
                    style={{ ...asset?.asset_color }}
                    onClick={() => onAssetClick(assetName)}
                >
                    {assetName}
                </div>
                <div
                    className={clsx(classes.categoryZones, {
                        categoryOpen: asset.open,
                    })}
                >
                    {assetCategoryBlocks}
                </div>
            </div>
        );
    }

    return (
        <div className={classes.scenePreviewsContainer} style={{ top: `${menu * 40}px` }}>
            <div
                className={clsx(classes.scenePreviews, {
                    [classes.sceneOpen]: open,
                })}
            >
                {blocks}
            </div>
        </div>
    );
};

export { ScenePreviews };
